/**
 * One tab owns the save (content-schemas §11.2 "Nhiều tab"): the owner holds the Web Lock `lct-save-owner`.
 * A second tab shows "đang mở ở tab khác"; its "Chơi ở tab này" asks the owner over a BroadcastChannel to
 * flush and let go, and steals the lock if the owner does not answer (frozen or discarded tab).
 * Without Web Locks every tab simply plays; the save's revision check (StaleWriteError) still guards writes.
 */

const LOCK = 'lct-save-owner';
const TAKEOVER = 'takeover';
const TAKEOVER_WAIT_MS = 3000;

export interface TabLockEnv {
  locks: Pick<LockManager, 'request'> | undefined;
  channel: () => Pick<BroadcastChannel, 'postMessage' | 'close'> & {
    onmessage: ((e: MessageEvent) => void) | null;
  };
}

const browserEnv = (): TabLockEnv => ({
  locks: typeof navigator !== 'undefined' && 'locks' in navigator ? navigator.locks : undefined,
  channel: () => new BroadcastChannel(LOCK),
});

export interface SaveOwnership {
  /** Runs before the lock is handed to another tab (flush the save, stop writing). */
  beforeRelease: (fn: () => Promise<void>) => void;
  /** Another tab took over: this one must not write any more. */
  onLost: (fn: () => void) => () => void;
  isLost: () => boolean;
}

/** Becomes the save owner, or returns `null` when another tab already is. */
export async function claimSaveOwnership(env: TabLockEnv = browserEnv()): Promise<SaveOwnership | null> {
  const listeners = new Set<() => void>();
  let lost = false;
  let beforeRelease: () => Promise<void> = () => Promise.resolve();
  const loseIt = () => {
    if (lost) return;
    lost = true;
    for (const fn of listeners) fn();
  };
  const ownership: SaveOwnership = {
    beforeRelease: (fn) => {
      beforeRelease = fn;
    },
    onLost: (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    isLost: () => lost,
  };
  if (!env.locks) return ownership;

  let release: () => void = () => undefined;
  const held = await new Promise<boolean>((resolve) => {
    env.locks
      ?.request(LOCK, { ifAvailable: true }, (lock) => {
        if (!lock) {
          resolve(false);
          return undefined;
        }
        resolve(true);
        // Hold the lock for the life of the page, until another tab asks for it.
        return new Promise<void>((r) => {
          release = r;
        });
      })
      // Stolen by a tab whose polite request went unanswered.
      .catch(loseIt);
  });
  if (!held) return null;

  const channel = env.channel();
  channel.onmessage = (e: MessageEvent) => {
    if (e.data !== TAKEOVER || lost) return;
    void beforeRelease().finally(() => {
      loseIt();
      release();
      channel.close();
    });
  };
  return ownership;
}

/**
 * "Chơi ở tab này": ask the owner to flush and release, wait for the lock (stealing it after a short wait),
 * then let go again so the reloaded page can claim it normally.
 */
export async function takeOverSave(env: TabLockEnv = browserEnv(), waitMs = TAKEOVER_WAIT_MS): Promise<void> {
  if (!env.locks) return;
  const locks = env.locks;
  const channel = env.channel();
  channel.postMessage(TAKEOVER);
  channel.close();
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, waitMs);
  try {
    await locks.request(LOCK, { signal: controller.signal }, () => undefined);
  } catch {
    await locks.request(LOCK, { steal: true }, () => undefined);
  } finally {
    clearTimeout(timer);
  }
}
